/**
 * API Client
 * Centralized API client using axios with the centralized configuration
 */

import axios, {AxiosResponse} from 'axios';
import {apiConfig, buildApiUrl, getEndpoint} from '@/config/api';
import {toast} from '@/hooks/use-toast';
import type {
  CreateParticipantRemovalRequest,
  ParticipantRemovalsQuery,
} from '@/features/participants/types/participantRemovals';
function sortProjectsByCreatedAtDesc<T extends { created_at?: string | null }>(projects: T[]): T[] {
  return [...projects].sort((a, b) => {
    const aTime = a.created_at ? Date.parse(a.created_at) : 0;
    const bTime = b.created_at ? Date.parse(b.created_at) : 0;
    return bTime - aTime;
  });
}

/**
 * Append a query param when the value is meaningful. Falsy values
 * (undefined/null/0/'') are skipped; `trim` trims strings (skipping
 * whitespace-only), and `skipAll` drops the sentinel value `'all'`.
 */
function appendQueryParam(
  qp: URLSearchParams,
  key: string,
  value: unknown,
  opts?: {trim?: boolean; skipAll?: boolean},
): void {
  if (!value) return;
  // Only primitives are stringified; objects become '' rather than '[object Object]'.
  let str =
    typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
      ? String(value)
      : '';
  if (opts?.trim) str = str.trim();
  if (!str) return;
  if (opts?.skipAll && str === 'all') return;
  qp.append(key, str);
}

/** Stored in `projects.email_template_config` (JSONB). Tab keys hold subject/body; hours drive participant reminder scheduling when the feature flag is on. */
export type ProjectEmailTemplateConfig = {
  booking_confirmation?: {subject?: string; body?: string};
  booking_confirmation_self_booking?: {subject?: string; body?: string};
  reschedule_confirmation?: {subject?: string; body?: string};
  cancellation_confirmation?: {subject?: string; body?: string};
  moderator_change?: {subject?: string; body?: string};
  session_completed?: {subject?: string; body?: string};
  reminder?: {subject?: string; body?: string};
  no_show?: {subject?: string; body?: string};
  self_booking_invite?: {subject?: string; body?: string};
  self_booking_reminder?: {subject?: string; body?: string};
  participant_reminder_hours?: number[];
  self_booking_reminder_offsets?: Array<{type: 'after_sent' | 'before_expiry'; hours: number}>;
};

export type ParticipantEmailTemplateType =
  | 'booking_confirmation'
  | 'booking_confirmation_self_booking'
  | 'reschedule_confirmation'
  | 'cancellation_confirmation'
  | 'moderator_change'
  | 'session_completed'
  | 'reminder'
  | 'no_show'
  | 'self_booking_invite'
  | 'self_booking_reminder';

/**
 * Mirror of the backend `getProjectEmailTemplateOverride` check in
 * data-collector-be/src/services/emailService.js — a template is considered
 * configured iff both subject and body are non-empty after trim.
 */
export function isProjectEmailTemplateConfigured(
  project: Pick<Project, 'email_template_config'> | null | undefined,
  templateType: ParticipantEmailTemplateType,
): boolean {
  const item = project?.email_template_config?.[templateType];
  if (!item) return false;
  const subject = (item.subject ?? '').trim();
  const body = (item.body ?? '').trim();
  return subject.length > 0 && body.length > 0;
}

// Type definitions based on Supabase schema
export interface Project {
  end_date: any;
  start_date: any;
  id: string;
  name: string;
  description: string | null;
  status: string | null;
  dynamics_id: string;
  created_at: string;
  created_by: string;
  updated_at: string;
  sections?: any[] | null;
  compensation?: string | null;
  time_commitment?: string | null;
  location?: string | null;
  user_role?: string | null; // User's role in this project (from backend)
  user_role_source?: 'team' | 'creator' | null; // Source of the role
  email_template_config?: ProjectEmailTemplateConfig | null;
  /** Default true: moderators see every project session. False restricts them to assigned sessions. */
  allow_moderators_view_all_sessions?: boolean | null;
}

export interface ProjectCreate {
  name: string;
  description?: string | null;
  status?: string | null;
  dynamics_id?: string;
}

export interface ProjectUpdate {
  name?: string;
  description?: string | null;
  status?: string | null;
  dynamics_id?: string;
  start_date?: string | null;
  end_date?: string | null;
  sections?: any[] | null;
  compensation?: string | null;
  time_commitment?: string | null;
  location?: string | null;
  email_template_config?: ProjectEmailTemplateConfig | null;
  allow_moderators_view_all_sessions?: boolean | null;
}

export interface ApiResponse<T = any> {
  data: T;
  error?: string;
  message?: string;
}

export interface TasksResponse {
  tasks: Task[];
  total?: number;
  page?: number;
  limit?: number;
}

export interface TaskMetadataField {
  id?: string;
  name: string;
  type: 'text' | 'number' | 'date' | 'dropdown' | 'checkbox' | 'radio' | 'iteration';
  description?: string;
  required: boolean;
  unique?: boolean;
  options?: string[]; // For dropdown and radio types
  defaultPrefix?: string;
  // Set when the field list is flattened out of a task template snapshot, where
  // `name` alone is not unique (the same label repeats across sections).
  fieldKey?: string;
  stageName?: string;
  sectionName?: string;
  /** Configured total shown after the slash in an iteration field. */
  maxIterations?: number;
  /** Optional author guidance carried from the visual task builder. */
  instructions?: string;
  /** The field or section in the same station that gates this field. */
  dependsOn?: string;
  /** Hide a gated field rather than showing it disabled. */
  hideUntilUnlocked?: boolean;
  /** How the referenced prerequisite is evaluated. */
  unlockCondition?:
    | 'all-required'
    | 'any'
    | {field: string; equals: string | number | boolean};
  /** Instructions inherited from the containing section. */
  sectionInstructions?: string;
  /**
   * Display name of the station that must be completed before this field's
   * station opens. Set when the snapshot declares a multi-station sequence.
   */
  stageDependsOn?: string;
}

export type TaskModuleTaskStatus = 'draft' | 'complete';

export interface TaskModuleCreateTaskPayload {
  taskName: string;
  description?: string;
  status?: TaskModuleTaskStatus;
  sourceFileName?: string;
  templateSnapshot?: unknown;
  sessionData?: unknown;
}

export interface TaskModuleUpdateSessionPayload {
  sessionData: unknown;
}

export interface TaskModuleUpdateDefinitionPayload {
  taskName?: string;
  description?: string | null;
  status?: TaskModuleTaskStatus;
  templateSnapshot: unknown;
}

export interface Task {
  id: string;
  title: string;
  description: string | null;
  status: string | null;
  priority: string | null;
  due_date: string | null;
  project_id: string;
  assigned_to: string | null;
  assigned_moderator_id?: string | null;
  assigned_moderator_ids?: string[];
  metadata?: TaskMetadataField[];
  /** Present for tasks created through @onedata/task-be's Data Fields flow. */
  template_snapshot?: unknown;
  /** Task-level values saved from the Tasks screen (host/module response shapes). */
  session_data?: Record<string, unknown> | string;
  sessionData?: Record<string, unknown> | string;
  script?: string | null;
  is_active?: boolean;
  created_at: string;
  updated_at: string;
  // API response fields
  project_name?: string;
  project_dynamics_id?: string;
  moderator_name?: string;
  moderator_email?: string;
}

export interface SchedulesResponse {
  schedules: Schedule[];
  total?: number;
  page?: number;
  limit?: number;
}

export interface Schedule {
  id: string;
  project_id: string;
  moderator_id?: string;
  participant_id: string;
  title: string;
  description?: string;
  start_time: string;
  end_time: string;
  meeting_type?: string;
  location?: string;
  status: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  parent_session_id?: string | null;
  booking_group_id?: string | null;
  secondary_session?: Schedule;
  primary_session?: Schedule | null;
}

export interface ModeratorsResponse {
  moderators: Moderator[];
  total?: number;
  page?: number;
  limit?: number;
}

export interface Moderator {
  id: string;
  name: string;
  email_id: string;
  emp_id: string;
  bio: string | null;
  city: string | null;
  state: string | null;
  specialties: string[] | null;
  photo: string | null;
  photo_mime_type: string | null;
  is_active: boolean;
  user_id: string;
  created_at: string;
  updated_at: string;
}

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: apiConfig.baseURL,
  timeout: apiConfig.request.timeout,
  headers: apiConfig.request.headers,
});

// Public (unauthenticated) API client.
// Use this for endpoints that must work without login (e.g. booking links).
const publicApiClient = axios.create({
  baseURL: apiConfig.baseURL,
  timeout: apiConfig.request.timeout,
  headers: apiConfig.request.headers,
});

const VIEW_AS_STORAGE_KEY = 'admin_view_as_role';
const VIEW_AS_REQUEST_ROLES = new Set([
  'project_manager',
  'scheduler',
  'moderator',
  'participant',
]);

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;

      // Propagate admin "view as" context to backend for server-side filtering.
      const viewAsRoleRaw = localStorage.getItem(VIEW_AS_STORAGE_KEY);
      const viewAsRole = (viewAsRoleRaw || '').toLowerCase().trim();
      if (VIEW_AS_REQUEST_ROLES.has(viewAsRole)) {
        config.headers['X-View-As-Role'] = viewAsRole;
      } else {
        delete config.headers['X-View-As-Role'];
      }
    }
    
    // If data is FormData, let axios handle Content-Type automatically
    // Don't set Content-Type header manually - browser will set it with boundary
    if (config.data instanceof FormData) {
      // Remove any manually set Content-Type to let browser set it with boundary
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    // Surface soft warnings the backend attaches to successful booking/reschedule
    // responses (e.g. reminder template missing — booking proceeds, reminder is suppressed).
    const reminderWarning = response?.data?.warnings?.reminder;
    if (reminderWarning?.reminder_template_missing) {
      toast({
        title: 'Reminder template not configured',
        description:
          reminderWarning.message ||
          'Configure the reminder template before reminders can be sent.',
      });
    }
    return response;
  },
  (error) => {
    // Surface "missing email template" gating from the backend as a toast for any
    // booking/cancel/reschedule/no-show/complete call — the underlying promise
    // still rejects so the caller can stop its own flow. The error is also
    // flagged so callers can skip their own duplicate error toast.
    if (
      error.response?.status === 422 &&
      error.response?.data?.code === 'EMAIL_TEMPLATE_NOT_CONFIGURED'
    ) {
      toast({
        title: 'Email template not configured',
        description:
          error.response.data?.error ||
          'Go to Email Setup Tab to set it up.',
        variant: 'destructive',
      });
      (error as {__emailTemplateToastShown?: boolean}).__emailTemplateToastShown = true;
    }

    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      // Redirect to auth page (avoid non-existent /login and prevent loops)
      try {
        const currentPath = window.location?.pathname || '';
        if (!currentPath.startsWith('/auth')) {
          // Guard against rapid multiple redirects
          if (!(window as any).__authRedirecting) {
            (window as any).__authRedirecting = true;
            window.location.replace('/auth');
          }
        }
      } catch {
        // best-effort fallback
        window.location.replace('/auth');
      }
    }
    return Promise.reject(error);
  }
);

/**
 * True when the response interceptor has already toasted an
 * EMAIL_TEMPLATE_NOT_CONFIGURED 422. Use in caller .catch() blocks to skip
 * a duplicate generic error toast.
 */
export function isEmailTemplateErrorHandled(err: unknown): boolean {
  return Boolean(
    (err as {__emailTemplateToastShown?: boolean} | null | undefined)
      ?.__emailTemplateToastShown,
  );
}

const MULTIPART_FILE_HEADERS = {'Content-Type': 'multipart/form-data'};

const postTaskModuleFile = (url: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.post(url, formData, {headers: MULTIPART_FILE_HEADERS});
};

// API methods using the centralized configuration
export const api = {
  // Authentication
  auth: {
    register: (data) => apiClient.post(apiConfig.endpoints.auth.register, data),
    login: (data) => apiClient.post(apiConfig.endpoints.auth.login, data),
    logout: () => apiClient.post(apiConfig.endpoints.auth.logout),
    me: () => apiClient.get(apiConfig.endpoints.auth.me),
    updatePassword: (data) =>
      apiClient.put(apiConfig.endpoints.auth.updatePassword, data),
    sso: {
      callback: (data) =>
        apiClient.post(apiConfig.endpoints.auth.sso.callback, data),
      centific: apiConfig.endpoints.auth.sso.centific,
    },
    entra: {
      callback: (data) =>
        apiClient.post(apiConfig.endpoints.auth.entra.callback, data),
    },
  },

  // User management
  users: {
    getProfile: () => apiClient.get(apiConfig.endpoints.users.profile),
    updateProfile: (data) =>
      apiClient.put(apiConfig.endpoints.users.update, data),
    deleteAccount: () => apiClient.delete(apiConfig.endpoints.users.delete),
    create: (data) => apiClient.post(apiConfig.endpoints.users.create, data),
    bulkCreate: (data) =>
      apiClient.post(apiConfig.endpoints.users.bulkCreate, data),
    getByEmail: (email) =>
      apiClient.get(apiConfig.endpoints.users.getByEmail(email)),
    updateRole: (userId, data) =>
      apiClient.put(apiConfig.endpoints.users.updateRole(userId), data),
  },

  // User roles
  userRoles: {
    get: (userId) => apiClient.get(apiConfig.endpoints.userRoles.get(userId)),
    update: (data) => apiClient.put(apiConfig.endpoints.userRoles.update, data),
  },

  // Moderators
  moderators: {
    // No caller of this endpoint ever wants server-side pagination — every
    // caller expects the full moderators directory — so default the limit
    // high rather than let it silently fall back to the backend's default
    // of 10 (which was truncating every consumer, including the Moderators
    // admin page itself, to the 10 most-recently-created moderators).
    list: (options?: {refresh?: boolean; limit?: number}) =>
      apiClient.get(apiConfig.endpoints.moderators.list, {
        params: {
          limit: options?.limit ?? 1000,
          ...(options?.refresh ? {refresh: 'true'} : {}),
        },
      }),
    get: (id) => apiClient.get(apiConfig.endpoints.moderators.get(id)),
    getByEmail: (email) =>
      apiClient.get(apiConfig.endpoints.moderators.byEmail(email)),
    getByEmpId: (empId) =>
      apiClient.get(apiConfig.endpoints.moderators.byEmpId(empId)),
    getByIds: (ids) =>
      apiClient.get(apiConfig.endpoints.moderators.byIds, {
        params: {ids: JSON.stringify(ids)},
      }),
    getActive: () => apiClient.get(apiConfig.endpoints.moderators.active),
    getByProject: (projectId: string) =>
      apiClient.get(apiConfig.endpoints.moderators.byProject(projectId)),
    // The current moderator's own add/remove events on upcoming sessions in
    // this project. Self-scoped by the auth token — no user id is passed.
    getMySessionChanges: (projectId: string) =>
      apiClient.get(apiConfig.endpoints.moderators.mySessionChanges(projectId)),
    create: (data) =>
      apiClient.post(apiConfig.endpoints.moderators.create, data),
    update: (id, data) =>
      apiClient.put(apiConfig.endpoints.moderators.update(id), data),
    delete: (id) => apiClient.delete(apiConfig.endpoints.moderators.delete(id)),
    deletePermanent: (id) => apiClient.delete(`/moderators/${id}/permanent`),
    register: (data) =>
      apiClient.post(apiConfig.endpoints.moderators.register, data),
  },

  // Participants
  participants: {
    list: (params?: {
      page?: number;
      limit?: number;
      home_id?: string | number;
      user_name?: string;
      project_id?: string;
      homeId?: string | number;
      username?: string;
      search?: string;
      status?: string;
      status_bucket?: string;
      gender?: string;
      participant_statuses?: string;
      participant_types?: string;
      relationships?: string;
      sort_by?: string;
      sort_dir?: string;
      assigned_scheduler_id?: string;
      // JSON-encoded { [columnId]: string[] } of per-column filters.
      column_filters?: string;
    }) => {
      const queryParams = new URLSearchParams();
      appendQueryParam(queryParams, 'page', params?.page);
      appendQueryParam(queryParams, 'limit', params?.limit);
      const resolvedHomeId = params?.home_id ?? params?.homeId;
      const resolvedUserName = params?.user_name ?? params?.username;
      appendQueryParam(queryParams, 'home_id', resolvedHomeId, {skipAll: true});
      appendQueryParam(queryParams, 'user_name', resolvedUserName, {skipAll: true});
      appendQueryParam(queryParams, 'project_id', params?.project_id, {skipAll: true});
      appendQueryParam(queryParams, 'search', params?.search, {trim: true});
      // Server-side filter/sort params (added for server-side pagination).
      appendQueryParam(queryParams, 'status', params?.status, {skipAll: true});
      appendQueryParam(queryParams, 'status_bucket', params?.status_bucket);
      appendQueryParam(queryParams, 'gender', params?.gender);
      // Advanced (multi-select) filter dialog selections, as comma-separated
      // display labels matched server-side against the grid columns.
      appendQueryParam(queryParams, 'participant_statuses', params?.participant_statuses);
      appendQueryParam(queryParams, 'participant_types', params?.participant_types);
      appendQueryParam(queryParams, 'relationships', params?.relationships);
      appendQueryParam(queryParams, 'column_filters', params?.column_filters);
      appendQueryParam(queryParams, 'sort_by', params?.sort_by);
      appendQueryParam(queryParams, 'sort_dir', params?.sort_dir);
      const queryString = queryParams.toString();
      const url = queryString
        ? `${apiConfig.endpoints.participants.list}?${queryString}`
        : apiConfig.endpoints.participants.list;
      return apiClient.get(url);
    },
    get: (id: string, projectId?: string) => {
      const base = apiConfig.endpoints.participants.get(id);
      if (projectId && projectId !== 'all') {
        return apiClient.get(
          `${base}?project_id=${encodeURIComponent(projectId)}`
        );
      }
      return apiClient.get(base);
    },
    getByEmail: (email) =>
      apiClient.get(apiConfig.endpoints.participants.byEmail(email)),
    create: (data) =>
      apiClient.post(apiConfig.endpoints.participants.create, data),
    createBulk: (data) =>
      apiClient.post(apiConfig.endpoints.participants.createBulk, data),
    update: (id, data) =>
      apiClient.put(apiConfig.endpoints.participants.update(id), data),
    delete: (id) =>
      apiClient.delete(apiConfig.endpoints.participants.delete(id)),
    removeFromProject: (id, projectId) =>
      apiClient.delete(apiConfig.endpoints.participants.removeFromProject(id, projectId)),
    createRemoval: (
      id: string,
      projectId: string,
      data: CreateParticipantRemovalRequest,
    ) =>
      apiClient.post(
        apiConfig.endpoints.participants.createRemoval(id, projectId),
        data,
      ),
    listRemovalHistory: (projectId: string, params?: ParticipantRemovalsQuery) =>
      apiClient.get(apiConfig.endpoints.participants.removalHistory(projectId), {
        params,
      }),
    exportRemovals: (projectId: string, params?: Omit<ParticipantRemovalsQuery, 'page' | 'limit'>) =>
      apiClient.get(apiConfig.endpoints.participants.exportRemovals(projectId), {
        params,
        responseType: 'blob',
      }),
    resetPassword: (id) => apiClient.post(`/participants/${id}/reset-password`),
    sendBookingLink: (participantId, data: {
      project_id: string;
      session_type_id?: string;
      moderator_id?: string;
      moderator_ids?: string[];
      session_type_ids?: string[];
      expires_at?: string;
      display_time_zone?: string;
      custom_message?: string;
      cancel_active_schedules?: boolean;
    }) => apiClient.post(apiConfig.endpoints.participants.sendBookingLink(participantId), data),
    bulkSendBookingLink: (data: {
      project_id: string;
      session_type_id: string;
      participant_ids?: string[];
      select_all_matching?: boolean;
      excluded_participant_ids?: string[];
      search?: string;
      expires_at?: string;
      display_time_zone?: string;
      custom_message?: string;
    }) => apiClient.post(apiConfig.endpoints.participants.bulkSendBookingLink, data),
    assignScheduler: (data) =>
      apiClient.post(apiConfig.endpoints.participants.assignScheduler, data),
    statusCards: (projectId: string) =>
      apiClient.get(
        `${apiConfig.endpoints.participants.stats.statusCards}?project_id=${encodeURIComponent(
          projectId
        )}`
      ),
    filterOptions: (projectId: string, options?: {refresh?: boolean}) =>
      apiClient.get(
        `${apiConfig.endpoints.participants.filterOptions}?project_id=${encodeURIComponent(
          projectId
        )}${options?.refresh ? '&refresh=true' : ''}`
      ),
    demographicsTable: (
      projectId: string,
      options?: {page?: number; limit?: number; refresh?: boolean},
    ) =>
      apiClient.get(`/participants/project/${projectId}/demographics-table`, {
        params: {
          page: options?.page,
          limit: options?.limit,
          ...(options?.refresh ? {refresh: 'true'} : {}),
        },
      }),
    columnValues: (projectId: string, columns: string[], options?: {refresh?: boolean}) =>
      apiClient.get(
        `/participants/project/${projectId}/column-values?columns=${encodeURIComponent(
          columns.join(','),
        )}${options?.refresh ? '&refresh=true' : ''}`,
      ),
    projectParticipantDemographicAnswers: (
      projectId: string,
      participantId: string,
    ) =>
      apiClient.get(
        `/participants/project/${projectId}/participant/${participantId}/demographic-answers`,
      ),
    projectParticipantSubmitRelationshipContext: (
      projectId: string,
      participantId: string,
      taskId?: string | null,
      scheduleId?: string | null,
    ) => {
      const params = new URLSearchParams();
      if (taskId != null && String(taskId).trim() !== '') {
        params.set('task_id', String(taskId).trim());
      }
      if (scheduleId != null && String(scheduleId).trim() !== '') {
        params.set('schedule_id', String(scheduleId).trim());
      }
      const q = params.toString() ? `?${params.toString()}` : '';
      return apiClient.get(
        `/participants/project/${projectId}/participant/${participantId}/submit-relationship-context${q}`,
      );
    },
    getContactAttempts: (id: string, projectId: string) =>
      apiClient.get(`/participants/${id}/contact-attempts?project_id=${encodeURIComponent(projectId)}`),
    logContactAttempt: (id: string, data: { project_id: string; outcome: string; notes?: string }) =>
      apiClient.post(`/participants/${id}/contact-attempts`, data),
    updateScheduleStatus: (id: string, data: { project_id: string; schedule_status: string }) =>
      apiClient.patch(`/participants/${id}/schedule-status`, data),
    getHistory: (id: string, projectId: string, scheduleId?: string) => {
      const qs = new URLSearchParams({ project_id: projectId });
      if (scheduleId) qs.set('scheduleId', scheduleId);
      return apiClient.get(`/participants/${id}/history?${qs.toString()}`);
    },
    /** Batch note-counts for many participants in one request (badge preload). */
    noteCounts: (projectId: string, participantIds: string[]) =>
      apiClient.get('/participants/note-counts', {
        params: {project_id: projectId, participant_ids: participantIds.join(',')},
      }),
    addNote: (id: string, data: { project_id: string; note: string }) =>
      apiClient.post(`/participants/${id}/notes`, data),
    getUploadSchema: (projectId: string) =>
      apiClient.get(`${apiConfig.endpoints.participants.uploadSchema}?project_id=${encodeURIComponent(projectId)}`),
  },

  // Projects - Enhanced with proper typing and error handling
  projects: {
    list: async (): Promise<AxiosResponse<ApiResponse<Project[]>>> => {
      const response = await apiClient.get(apiConfig.endpoints.projects.list);
      const payload = response.data as any;

      if (Array.isArray(payload)) {
        response.data = sortProjectsByCreatedAtDesc(payload) as any;
      } else if (payload && Array.isArray(payload.data)) {
        payload.data = sortProjectsByCreatedAtDesc(payload.data);
      }

      return response;
    },
    get: (id: string): Promise<AxiosResponse<ApiResponse<Project>>> =>
      apiClient.get(apiConfig.endpoints.projects.get(id)),
    getRole: (id: string): Promise<AxiosResponse<{role: string | null; source: string | null; hasAccess: boolean}>> =>
      apiClient.get(apiConfig.endpoints.projects.get(id) + '/role'),
    getRolesBatch: (projectIds: string[]): Promise<AxiosResponse<Record<string, {role: string | null; source: string | null; hasAccess: boolean}>>> =>
      apiClient.post('/projects/roles/batch', { projectIds }),
    create: (
      data: ProjectCreate
    ): Promise<AxiosResponse<ApiResponse<Project>>> =>
      apiClient.post(apiConfig.endpoints.projects.create, data),
    update: (
      id: string,
      data: ProjectUpdate
    ): Promise<AxiosResponse<ApiResponse<Project>>> =>
      apiClient.put(apiConfig.endpoints.projects.update(id), data),
    delete: (id: string): Promise<AxiosResponse<ApiResponse<void>>> =>
      apiClient.delete(apiConfig.endpoints.projects.delete(id)),
    activate: (id: string): Promise<AxiosResponse<ApiResponse<Project>>> =>
      apiClient.post(apiConfig.endpoints.projects.get(id) + '/activate'),
    complete: (id: string): Promise<AxiosResponse<ApiResponse<Project>>> =>
      apiClient.post(apiConfig.endpoints.projects.get(id) + '/complete'),
    getHistory: (id: string) => apiClient.get(apiConfig.endpoints.projects.history(id)),
    getEmailTemplateHistory: (id: string) =>
      apiClient.get(apiConfig.endpoints.projects.emailTemplateHistory(id)),
    migrateDynamicsId: (): Promise<AxiosResponse<ApiResponse<any>>> =>
      apiClient.post(apiConfig.endpoints.projects.migrateDynamicsId),
  },

  // Project payment (bonus / partial payment) configuration — OD-1089
  paymentConfig: {
    get: (projectId: string): Promise<AxiosResponse<ApiResponse<any>>> =>
      apiClient.get(apiConfig.endpoints.paymentConfig.get(projectId)),
    addSubcategory: (
      projectId: string,
      data: {category: string; name: string; default_amount: number; enabled: boolean}
    ): Promise<AxiosResponse<ApiResponse<any>>> =>
      apiClient.post(apiConfig.endpoints.paymentConfig.addSubcategory(projectId), data),
    updateSubcategory: (
      projectId: string,
      id: string,
      data: {
        default_amount?: number;
        enabled?: boolean;
        max_per_participant?: number | null;
        rule_config?: unknown;
      }
    ): Promise<AxiosResponse<ApiResponse<any>>> =>
      apiClient.patch(apiConfig.endpoints.paymentConfig.updateSubcategory(projectId, id), data),
  },

  // Scheduler queue (server-side filter/sort/paginate)
  scheduler: {
    queue: (params?: Record<string, unknown>) =>
      apiClient.get(apiConfig.endpoints.scheduler.queue, {params}),
    bookedQueue: (params?: Record<string, unknown>) =>
      apiClient.get(apiConfig.endpoints.scheduler.bookedQueue, {params}),
    bookedStats: (params?: Record<string, unknown>) =>
      apiClient.get(apiConfig.endpoints.scheduler.bookedStats, {params}),
    sessionTypesInvitable: (params?: Record<string, unknown>) =>
      apiClient.get(apiConfig.endpoints.scheduler.sessionTypesInvitable, {params}),
    sessionTypeInvitableParticipants: (
      sessionTypeId: string,
      params?: Record<string, unknown>,
    ) =>
      apiClient.get(
        apiConfig.endpoints.scheduler.sessionTypeInvitableParticipants(sessionTypeId),
        {params},
      ),
  },

  // Per-user UI preferences (column visibility persisted across logins/devices)
  userPreferences: {
    getColumns: () => apiClient.get('/user-preferences/columns'),
    saveColumns: (screen: string, preferences: Record<string, boolean>) =>
      apiClient.put(`/user-preferences/columns/${screen}`, {preferences}),
  },

  // Schedules
  schedules: {
    list: (params?: any) => apiClient.get(apiConfig.endpoints.schedules.list, {params}),
    statusCounts: (params?: {project_id?: string}) =>
      apiClient.get(apiConfig.endpoints.schedules.statusCounts, {params}),
    consolidatedExport: (params?: Record<string, unknown> & { project_id?: string }) =>
      apiClient.get(apiConfig.endpoints.schedules.consolidatedExport, { params }),
    contactLogExport: (params?: Record<string, unknown> & { project_id?: string }) =>
      apiClient.get(apiConfig.endpoints.schedules.contactLogExport, { params }),
    get: (id) => apiClient.get(apiConfig.endpoints.schedules.get(id)),
    getByParticipant: (participantId) =>
      apiClient.get(apiConfig.endpoints.schedules.byParticipant(participantId)),
    getByParticipants: (participantIds: string[], projectId?: string) =>
      apiClient.get(apiConfig.endpoints.schedules.byParticipants, {
        params: {
          participant_ids: participantIds.join(','),
          ...(projectId ? {project_id: projectId} : {}),
        },
      }),
    create: (data) =>
      apiClient.post(apiConfig.endpoints.schedules.create, data),
    assignGroupId: (data: {project_id: string; schedule_ids: string[]}) =>
      apiClient.post(apiConfig.endpoints.schedules.assignGroupId, data),
    bulkCancel: (
      data:
        | {
            project_id: string;
            schedule_ids: string[];
            payment: 'with' | 'without';
            category?: 'overbooking' | 'paused' | 'other';
            explanation: string;
            allow_future_booking: boolean;
          }
        | {
            project_id: string;
            schedule_ids: string[];
            cancellation_type:
              | 'With Partial Payment'
              | 'Without Partial Payment';
            explanation: string;
          },
    ) => apiClient.post(apiConfig.endpoints.schedules.bulkCancel, data),
    /**
     * OD-1277: cancel a whole group booking, or remove selected participants
     * from one. Which of the two it is comes from the selection — the server
     * derives `cancellationScope` from the data rather than trusting a flag.
     */
    groupCancel: (
      bookingGroupId: string,
      data:
        | {
            project_id: string;
            schedule_ids: string[];
            payment: 'with' | 'without';
            category?: string;
            explanation: string;
            allow_future_booking: boolean;
          }
        | {
            project_id: string;
            schedule_ids: string[];
            cancellation_type:
              | 'With Partial Payment'
              | 'Without Partial Payment';
            explanation: string;
          },
    ) =>
      apiClient.post(apiConfig.endpoints.schedules.groupCancel(bookingGroupId), data),
    createGroup: (data) =>
      apiClient.post(apiConfig.endpoints.schedules.createGroup, data),
    createSimple: (data) =>
      apiClient.post(apiConfig.endpoints.schedules.createSimple, data),
    update: (id, data) =>
      apiClient.put(apiConfig.endpoints.schedules.update(id), data),
    updateActualTimes: (
      id: string,
      data: {
        actual_start_time?: string;
        actual_end_time?: string;
        reason: string;
      },
    ) => apiClient.patch(apiConfig.endpoints.schedules.actualTimes(id), data),
    disqualify: (id: string, data: { reason: string }) =>
      apiClient.post(apiConfig.endpoints.schedules.disqualify(id), data),
    doNotHire: (id: string, data: { reason: string }) =>
      apiClient.post(apiConfig.endpoints.schedules.doNotHire(id), data),
    overbook: (id: string, data: { reason: string }) =>
      apiClient.post(apiConfig.endpoints.schedules.overbook(id), data),
    correctStatus: (
      id: string,
      data: { corrected_status: string; reason: string },
    ) => apiClient.post(apiConfig.endpoints.schedules.correctStatus(id), data),
    delete: (id) => apiClient.delete(apiConfig.endpoints.schedules.delete(id)),
    // New participant scheduling methods
    getAvailableModerators: (projectId) =>
      apiClient.get(
        apiConfig.endpoints.schedules.availableModerators(projectId)
      ),
    getAvailableSlots: (moderatorId, projectId, params) =>
      apiClient.get(
        apiConfig.endpoints.schedules.availableSlots(moderatorId, projectId),
        {params}
      ),
    getSlotCapacity: (params) =>
      apiClient.get(apiConfig.endpoints.schedules.slotCapacity, {params}),
    participantBook: (data) =>
      apiClient.post(apiConfig.endpoints.schedules.participantBook, data),
    participantCancel: (scheduleId, data) =>
      apiClient.patch(
        apiConfig.endpoints.schedules.participantCancel(scheduleId),
        data
      ),
    participantReschedule: (scheduleId, data) =>
      apiClient.patch(
        apiConfig.endpoints.schedules.participantReschedule(scheduleId),
        data
      ),
    participantActionValidate: (params) =>
      apiClient.get(apiConfig.endpoints.schedules.participantActionValidate, { params }),
    participantActionCancel: (data) =>
      apiClient.post(apiConfig.endpoints.schedules.participantActionCancel, data),
    generateCheckInCode: (scheduleId) =>
      apiClient.post(apiConfig.endpoints.schedules.generateCheckInCode(scheduleId)),
    verifyCheckInCode: (scheduleId, data) =>
      apiClient.post(apiConfig.endpoints.schedules.verifyCheckInCode(scheduleId), data),
    getCheckInStatus: (scheduleId) =>
      apiClient.get(apiConfig.endpoints.schedules.checkInStatus(scheduleId)),
    getParticipantMeetings: (participantId, params) =>
      apiClient.get(
        apiConfig.endpoints.schedules.participantMeetings(participantId),
        {params}
      ),
    addNote: (scheduleId, data: { body: string; authorName?: string; type?: string }) =>
      apiClient.post(apiConfig.endpoints.schedules.addNote(scheduleId), data),
    getSchedulerReminders: () =>
      apiClient.get(apiConfig.endpoints.schedules.schedulerReminders),
  },

  // Moderator Availability
  moderatorAvailability: {
    list: (params?: { approved_only?: boolean; status?: string; project_id?: string }) => {
      const queryParams = new URLSearchParams();
      if (params?.approved_only) queryParams.append('approved_only', 'true');
      if (params?.status) queryParams.append('status', params.status);
      if (params?.project_id) queryParams.append('project_id', params.project_id);
      const queryString = queryParams.toString();
      return apiClient.get(`/moderator-availability${queryString ? `?${queryString}` : ''}`);
    },
    getByModerator: (moderatorId: string, approvedOnly?: boolean) => {
      const queryString = approvedOnly ? '?approved_only=true' : '';
      return apiClient.get(`/moderator-availability/moderator/${moderatorId}${queryString}`);
    },
    create: (data: any) => apiClient.post('/moderator-availability', data),
    bulkCreate: (data: any[]) => apiClient.post('/moderator-availability/bulk', { availabilities: data }),
    updatePeriod: (
      moderatorId: string,
      data: {
        project_id: string;
        previous_effective_from: string;
        previous_effective_to?: string | null;
        effective_from: string;
        effective_to?: string | null;
        slot_duration_minutes?: number;
        timezone?: string;
        status?: string;
        /** IANA zone the moderator is currently viewed in (e.g. "America/Los_Angeles") —
         * lets the backend label/group history entries by local weekday instead of the
         * raw UTC day_of_week bucket, which can shift when an evening slot crosses UTC
         * midnight (OD-892). Omit to keep the prior (UTC-labeled) behavior. */
        display_timezone?: string;
        weekly_slots: Array<{
          day_of_week: number;
          start_time: string;
          end_time: string;
          slot_duration_minutes?: number;
          timezone?: string;
          status?: string;
        }>;
      },
    ) => apiClient.put(`/moderator-availability/period/${moderatorId}`, data),
    update: (id: string, data: any) => apiClient.put(`/moderator-availability/${id}`, data),
    delete: (id: string) => apiClient.delete(`/moderator-availability/${id}`),
    checkDelete: (availabilityIds: string[]) =>
      apiClient.post('/moderator-availability/check-delete', {
        availability_ids: availabilityIds,
      }),
    deleteByModerator: (moderatorId: string) => apiClient.delete(`/moderator-availability/moderator/${moderatorId}`),
    // displayTimezone (OD-892): so the "Availability Approved"/"Rejected" history
    // entry labels the correct local weekday, not the raw UTC day_of_week bucket.
    // Omitted entirely (not sent as an explicit undefined body) when absent, so
    // older callers keep making the exact same request as before.
    approve: (id: string, displayTimezone?: string) =>
      displayTimezone
        ? apiClient.patch(`/moderator-availability/${id}/approve`, { display_timezone: displayTimezone })
        : apiClient.patch(`/moderator-availability/${id}/approve`),
    requestDelete: (id: string) => apiClient.patch(`/moderator-availability/${id}/request-delete`),
    reject: (id: string, displayTimezone?: string) =>
      displayTimezone
        ? apiClient.patch(`/moderator-availability/${id}/reject`, { display_timezone: displayTimezone })
        : apiClient.patch(`/moderator-availability/${id}/reject`),
    getHistory: (moderatorId: string, projectId: string, page = 1, pageSize = 10) =>
      apiClient.get(`/moderator-availability/moderator/${moderatorId}/history`, {
        params: {project_id: projectId, page, pageSize},
      }),
  },

  // Surveys
  surveys: {
    list: () => apiClient.get(apiConfig.endpoints.surveys.list),
    get: (id) => apiClient.get(apiConfig.endpoints.surveys.get(id)),
    create: (data) => apiClient.post(apiConfig.endpoints.surveys.create, data),
    update: (id, data) =>
      apiClient.put(apiConfig.endpoints.surveys.update(id), data),
    delete: (id) => apiClient.delete(apiConfig.endpoints.surveys.delete(id)),
    responses: {
      list: (surveyId) =>
        apiClient.get(apiConfig.endpoints.surveys.responses.list(surveyId)),
      create: (surveyId, data) =>
        apiClient.post(
          apiConfig.endpoints.surveys.responses.create(surveyId),
          data
        ),
    },
  },

  uploadNotifications: {
    list: (filter?: 'all' | 'failed', projectId?: string) =>
      apiClient.get(apiConfig.endpoints.uploadNotifications.list, {
        params: {
          ...(filter ? {filter} : {}),
          ...(projectId ? {project_id: projectId} : {}),
        },
      }),
    report: (data: unknown) =>
      apiClient.post(apiConfig.endpoints.uploadNotifications.report, data),
    markRead: (id: string) =>
      apiClient.patch(apiConfig.endpoints.uploadNotifications.markRead(id)),
    markAllRead: (projectId?: string) =>
      apiClient.post(
        apiConfig.endpoints.uploadNotifications.markAllRead,
        projectId ? {project_id: projectId} : {},
      ),
    getBatch: (ref: string) =>
      apiClient.get(apiConfig.endpoints.uploadNotifications.getBatch(ref)),
    actionItems: (projectId: string) =>
      apiClient.get(apiConfig.endpoints.uploadNotifications.actionItems, {
        params: {project_id: projectId},
      }),
    dismissBatch: (id: string) =>
      apiClient.post(apiConfig.endpoints.uploadNotifications.dismissBatch(id)),
  },

  // Tasks
  tasks: {
    list: (params?: { project_id?: string }) => 
      apiClient.get(apiConfig.endpoints.tasks.list, { params }),
    get: (id) => apiClient.get(apiConfig.endpoints.tasks.get(id)),
    byProject: (projectId) => apiClient.get(apiConfig.endpoints.tasks.byProject(projectId)),
    create: (data) => apiClient.post(apiConfig.endpoints.tasks.create, data),
    update: (id, data) =>
      apiClient.put(apiConfig.endpoints.tasks.update(id), data),
    delete: (id) => apiClient.delete(apiConfig.endpoints.tasks.delete(id)),
    toggleActive: (id, isActive) =>
      apiClient.patch(apiConfig.endpoints.tasks.toggleActive(id), { is_active: isActive }),
  },

  // Task View Templates (@onedata/task-be)
  taskModule: {
    listProjects: () => apiClient.get(apiConfig.endpoints.taskModule.projects),
    getTemplate: (projectId: string) =>
      apiClient.get(apiConfig.endpoints.taskModule.get(projectId)),
    previewTemplate: (projectId: string, file: File) =>
      postTaskModuleFile(apiConfig.endpoints.taskModule.preview(projectId), file),
    publishTemplate: (projectId: string, file: File) =>
      postTaskModuleFile(apiConfig.endpoints.taskModule.publish(projectId), file),
    dataFields: {
      get: (projectId: string) =>
        apiClient.get(apiConfig.endpoints.taskModule.dataFields.get(projectId)),
      preview: (projectId: string, file: File) =>
        postTaskModuleFile(apiConfig.endpoints.taskModule.dataFields.preview(projectId), file),
      publish: (projectId: string, file: File) =>
        postTaskModuleFile(apiConfig.endpoints.taskModule.dataFields.publish(projectId), file),
    },
    tasks: {
      list: (projectId: string) =>
        apiClient.get(apiConfig.endpoints.taskModule.tasks.list(projectId)),
      create: (projectId: string, data: TaskModuleCreateTaskPayload) =>
        apiClient.post(apiConfig.endpoints.taskModule.tasks.create(projectId), data),
      get: (taskId: string) => apiClient.get(apiConfig.endpoints.taskModule.tasks.get(taskId)),
      update: (taskId: string, data: TaskModuleUpdateSessionPayload) =>
        apiClient.patch(apiConfig.endpoints.taskModule.tasks.update(taskId), data),
      updateDefinition: (taskId: string, data: TaskModuleUpdateDefinitionPayload) =>
        apiClient.patch(
          apiConfig.endpoints.taskModule.tasks.updateDefinition(taskId),
          data,
        ),
      delete: (taskId: string) =>
        apiClient.delete(apiConfig.endpoints.taskModule.tasks.delete(taskId)),
    },
  },

  // Project Moderators
  projectModerators: {
    list: (params?: {project_id?: string; limit?: number}) => {
      const queryParams = new URLSearchParams();
      if (params?.project_id) queryParams.append('project_id', params.project_id);
      if (params?.limit) queryParams.append('limit', String(params.limit));
      const queryString = queryParams.toString();
      const url = queryString
        ? `${apiConfig.endpoints.projectModerators.list}?${queryString}`
        : apiConfig.endpoints.projectModerators.list;
      return apiClient.get(url);
    },
    get: (id) => apiClient.get(apiConfig.endpoints.projectModerators.get(id)),
    create: (data) =>
      apiClient.post(apiConfig.endpoints.projectModerators.create, data),
    update: (id, data) =>
      apiClient.put(apiConfig.endpoints.projectModerators.update(id), data),
    delete: (id) =>
      apiClient.delete(apiConfig.endpoints.projectModerators.delete(id)),
  },

  // Journal Activity
  journalActivity: {
    list: () => apiClient.get(apiConfig.endpoints.journalActivity.list),
    get: (id) => apiClient.get(apiConfig.endpoints.journalActivity.get(id)),
    getByHomeAndDate: (homeId, date) =>
      apiClient.get(
        apiConfig.endpoints.journalActivity.getByHomeAndDate(homeId, date)
      ),
    create: (data) =>
      apiClient.post(apiConfig.endpoints.journalActivity.create, data),
    createBulk: (data) =>
      apiClient.post(apiConfig.endpoints.journalActivity.createBulk, data),
    update: (id, data) =>
      apiClient.put(apiConfig.endpoints.journalActivity.update(id), data),
    delete: (id) =>
      apiClient.delete(apiConfig.endpoints.journalActivity.delete(id)),
    deleteByHomeAndDate: (homeId, date) =>
      apiClient.delete(
        apiConfig.endpoints.journalActivity.deleteByHomeAndDate(homeId, date)
      ),
  },

  // Journal media
  journalMedia: {
    create: (data) => apiClient.post('/journal-media', data),
    listByActivity: (activityId: string) =>
      apiClient.get(`/journal-media/activity/${activityId}`),
    countsByActivity: (activityId: string) =>
      apiClient.get(`/journal-media/activity/${activityId}/counts`),
  },

  // Feedback
  feedback: {
    list: () => apiClient.get(apiConfig.endpoints.feedback.list),
    get: (id) => apiClient.get(apiConfig.endpoints.feedback.get(id)),
    create: (data) => apiClient.post(apiConfig.endpoints.feedback.create, data),
    update: (id, data) =>
      apiClient.put(apiConfig.endpoints.feedback.update(id), data),
    delete: (id) => apiClient.delete(apiConfig.endpoints.feedback.delete(id)),
    responses: {
      list: (feedbackId) =>
        apiClient.get(apiConfig.endpoints.feedback.responses.list(feedbackId)),
      create: (feedbackId, data) =>
        apiClient.post(
          apiConfig.endpoints.feedback.responses.create(feedbackId),
          data
        ),
    },
  },

  // Health check
  health: () => apiClient.get(apiConfig.endpoints.health),

  // Activity logs
  activityLogs: {
    getUserLogs: (params) =>
      apiClient.get(apiConfig.endpoints.activityLogs.user, {params}),
    getAllLogs: (params) =>
      apiClient.get(apiConfig.endpoints.activityLogs.all, {params}),
  },

  // Booking decisions (OD-1102 follow-up)
  bookingDecisions: {
    list: (projectId: string) =>
      apiClient.get(apiConfig.endpoints.bookingDecisions.list, {
        params: {project_id: projectId},
      }),
    upsert: (data: {
      participant_id: string;
      project_id: string;
      session_type_id: string;
      decision: 'allowed' | 'blocked';
    }) => apiClient.post(apiConfig.endpoints.bookingDecisions.upsert, data),
  },

  // Schedule Locations
  scheduleLocations: {
    list: (options?: {refresh?: boolean}) =>
      apiClient.get(apiConfig.endpoints.scheduleLocations.list, {
        params: options?.refresh ? {refresh: 'true'} : undefined,
      }),
    get: (id) => apiClient.get(apiConfig.endpoints.scheduleLocations.get(id)),
    getByProject: (projectId, options?: {refresh?: boolean}) =>
      apiClient.get(apiConfig.endpoints.scheduleLocations.byProject(projectId), {
        params: options?.refresh ? {refresh: 'true'} : undefined,
      }),
    create: (data) =>
      apiClient.post(apiConfig.endpoints.scheduleLocations.create, data),
    update: (id, data) =>
      apiClient.put(apiConfig.endpoints.scheduleLocations.update(id), data),
    delete: (id) =>
      apiClient.delete(apiConfig.endpoints.scheduleLocations.delete(id)),
    toggle: (id) =>
      apiClient.patch(apiConfig.endpoints.scheduleLocations.toggle(id)),
  },

  // Session Types
  sessionTypes: {
    list: (options?: {refresh?: boolean}) =>
      apiClient.get(apiConfig.endpoints.sessionTypes.list, {
        params: options?.refresh ? {refresh: 'true'} : undefined,
      }),
    get: (id) => apiClient.get(apiConfig.endpoints.sessionTypes.get(id)),
    detailed: (options?: {refresh?: boolean}) =>
      apiClient.get(apiConfig.endpoints.sessionTypes.detailed, {
        params: options?.refresh ? {refresh: 'true'} : undefined,
      }),
    detailedByProject: (projectId: string, options?: {refresh?: boolean}) =>
      apiClient.get(apiConfig.endpoints.sessionTypes.byProjectDetailed(projectId), {
        params: options?.refresh ? {refresh: 'true'} : undefined,
      }),
    participantJobAssignmentsByProject: (projectId: string) =>
      apiClient.get(
        apiConfig.endpoints.sessionTypes.participantJobAssignmentsByProject(
          projectId
        )
      ),
    jobsByProject: (projectId: string) =>
      apiClient.get(apiConfig.endpoints.sessionTypes.jobsByProject(projectId)),
    refreshJobRate: (projectId: string, jobId: string | number) =>
      apiClient.post(
        apiConfig.endpoints.sessionTypes.refreshJobRate(projectId, jobId)
      ),
    getByProject: (projectId: string) =>
      apiClient.get(apiConfig.endpoints.sessionTypes.byProject(projectId)),
    create: (data) =>
      apiClient.post(apiConfig.endpoints.sessionTypes.create, data),
    update: (id, data) =>
      apiClient.put(apiConfig.endpoints.sessionTypes.update(id), data),
    delete: (id) =>
      apiClient.delete(apiConfig.endpoints.sessionTypes.delete(id)),
    toggle: (id) =>
      apiClient.patch(apiConfig.endpoints.sessionTypes.toggle(id)),
    getHistory: (id: string, page = 1, pageSize = 10) =>
      apiClient.get(apiConfig.endpoints.sessionTypes.history(id), {
        params: {page, pageSize},
      }),
  },

  // Submission Metadata
  submissionMetadata: {
    create: (data) =>
      apiClient.post(apiConfig.endpoints.submissionMetadata.create, data),
    update: (id, data) =>
      apiClient.put(apiConfig.endpoints.submissionMetadata.update(id), data),
    delete: (id, data?: any) =>
      apiClient.delete(apiConfig.endpoints.submissionMetadata.delete(id), {
        data,
      }),
    restore: (id, data?: any) =>
      apiClient.post(apiConfig.endpoints.submissionMetadata.restore(id), data),
    deleteBackup: (id, data?: any) =>
      apiClient.delete(apiConfig.endpoints.submissionMetadata.deleteBackup(id), {
        data,
      }),
    getTasks: (params?: { project_id?: string; task_id?: string }) => {
      const queryParams = new URLSearchParams();
      if (params?.project_id) queryParams.append('project_id', params.project_id);
      if (params?.task_id) queryParams.append('task_id', params.task_id);
      const queryString = queryParams.toString();
      const url = queryString
        ? `${apiConfig.endpoints.submissionMetadata.getTasks}?${queryString}`
        : apiConfig.endpoints.submissionMetadata.getTasks;
      return apiClient.get(url);
    },
    getSessions: (params?: { project_id?: string; session_type_id?: string }) => {
      const queryParams = new URLSearchParams();
      if (params?.project_id) queryParams.append('project_id', params.project_id);
      if (params?.session_type_id) queryParams.append('session_type_id', params.session_type_id);
      const queryString = queryParams.toString();
      const url = queryString
        ? `${apiConfig.endpoints.submissionMetadata.getSessions}?${queryString}`
        : apiConfig.endpoints.submissionMetadata.getSessions;
      return apiClient.get(url);
    },
    getById: (id) =>
      apiClient.get(apiConfig.endpoints.submissionMetadata.getById(id)),
    getDataUploads: (params?: {
      project_id?: string;
      search?: string;
      date_from?: string;
      date_to?: string;
      file_type?: string;
      booking_group_id?: string;
      include_backups?: boolean;
      page?: number;
      limit?: number;
    }) => {
      const queryParams = new URLSearchParams();
      if (params?.project_id) queryParams.append('project_id', params.project_id);
      if (params?.search) queryParams.append('search', params.search);
      if (params?.date_from) queryParams.append('date_from', params.date_from);
      if (params?.date_to) queryParams.append('date_to', params.date_to);
      if (params?.file_type) queryParams.append('file_type', params.file_type);
      if (params?.booking_group_id) queryParams.append('booking_group_id', params.booking_group_id);
      if (params?.include_backups !== undefined) queryParams.append('include_backups', String(params.include_backups));
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.limit) queryParams.append('limit', String(params.limit));
      const queryString = queryParams.toString();
      const url = queryString
        ? `${apiConfig.endpoints.submissionMetadata.getDataUploads}?${queryString}`
        : apiConfig.endpoints.submissionMetadata.getDataUploads;
      return apiClient.get(url);
    },
  },

  // Session Prep Drafts
  sessionPrepDrafts: {
    saveDraft: (data: {
      schedule_id: string;
      task_id: string;
      project_id: string;
      task_metadata: Record<string, any>;
    }) => apiClient.post(apiConfig.endpoints.sessionPrepDrafts.create, data),
    getDraft: (params: {
      schedule_id: string;
      task_id: string;
    }) => {
      const queryParams = new URLSearchParams();
      queryParams.append('schedule_id', params.schedule_id);
      queryParams.append('task_id', params.task_id);
      return apiClient.get(`${apiConfig.endpoints.sessionPrepDrafts.get}?${queryParams.toString()}`);
    },
    getDrafts: (params?: {project_id?: string; schedule_id?: string}) => {
      const queryParams = new URLSearchParams();
      if (params?.project_id) queryParams.append('project_id', params.project_id);
      if (params?.schedule_id) queryParams.append('schedule_id', params.schedule_id);
      const queryString = queryParams.toString();
      const url = queryString
        ? `${apiConfig.endpoints.sessionPrepDrafts.get}?${queryString}`
        : apiConfig.endpoints.sessionPrepDrafts.get;
      return apiClient.get(url);
    },
    getDraftsBySchedule: (scheduleId: string) =>
      apiClient.get(apiConfig.endpoints.sessionPrepDrafts.getBySchedule(scheduleId)),
    deleteDraft: (params: {
      schedule_id: string;
      task_id: string;
    }) => {
      const queryParams = new URLSearchParams();
      queryParams.append('schedule_id', params.schedule_id);
      queryParams.append('task_id', params.task_id);
      return apiClient.delete(`${apiConfig.endpoints.sessionPrepDrafts.delete}?${queryParams.toString()}`);
    },
  },

  // Team
  team: {
    getByProject: (projectId, params?: { role?: string; status?: string }) => {
      const queryParams = new URLSearchParams();
      if (params?.role) queryParams.append('role', params.role);
      if (params?.status) queryParams.append('status', params.status);
      const queryString = queryParams.toString();
      const url = queryString
        ? `${apiConfig.endpoints.team.byProject(projectId)}?${queryString}`
        : apiConfig.endpoints.team.byProject(projectId);
      return apiClient.get(url);
    },
    getStats: (projectId) =>
      apiClient.get(apiConfig.endpoints.team.stats(projectId)),
    getHistory: (projectId: string) =>
      apiClient.get(apiConfig.endpoints.team.history(projectId)),
    getSchedulers: (projectId?: string) =>
      apiClient.get(apiConfig.endpoints.team.getSchedulers(projectId)),
    addMember: (projectId, data) =>
      apiClient.post(apiConfig.endpoints.team.addMember(projectId), data),
    updateMember: (memberId, data) =>
      apiClient.put(apiConfig.endpoints.team.updateMember(memberId), data),
    removeMember: (memberId) =>
      apiClient.delete(apiConfig.endpoints.team.removeMember(memberId)),
    getAvailableUsers: (projectId, search = '') => {
      const url = `${apiConfig.endpoints.team.availableUsers}?projectId=${projectId}&search=${search}`;
      return apiClient.get(url);
    },
    acceptInvitation: (token: string) =>
      apiClient.get(apiConfig.endpoints.team.acceptInvitation(token)),
  },

  // Booking Links
  bookingLinks: {
    validate: (
      token: string,
      verificationToken?: string,
      query?: {
        reschedule?: boolean;
        cancel?: boolean;
        scheduleId?: string;
        action_token?: string;
      },
    ) => {
      const queryParams = new URLSearchParams();
      if (query?.reschedule) queryParams.append('reschedule', 'true');
      if (query?.cancel) queryParams.append('cancel', 'true');
      if (query?.scheduleId) queryParams.append('scheduleId', query.scheduleId);
      if (query?.action_token) queryParams.append('action_token', query.action_token);
      const qs = queryParams.toString();
      const url = qs
        ? `${apiConfig.endpoints.bookingLinks.validate(token)}?${qs}`
        : apiConfig.endpoints.bookingLinks.validate(token);
      return publicApiClient.get(url, {
        headers: verificationToken
          ? { 'X-Booking-Verification': verificationToken }
          : undefined,
      });
    },
    confirmEmail: (
      token: string,
      data: { email: string; confirm_email: string },
    ) =>
      publicApiClient.post(
        apiConfig.endpoints.bookingLinks.confirmEmail(token),
        data,
      ),
    sendOtp: (token: string, resend = false) =>
      publicApiClient.post(apiConfig.endpoints.bookingLinks.sendOtp(token), {
        resend,
      }),
    verifyOtp: (token: string, otp: string) =>
      publicApiClient.post(apiConfig.endpoints.bookingLinks.verifyOtp(token), {
        otp,
      }),
    getTimeSlots: (
      token: string,
      params: {
        session_type_id: string;
        date: string;
        reschedule_schedule_id?: string;
      },
      verificationToken?: string,
    ) => {
      const queryParams = new URLSearchParams();
      queryParams.append('session_type_id', params.session_type_id);
      queryParams.append('date', params.date);
      if (params.reschedule_schedule_id) {
        queryParams.append(
          'reschedule_schedule_id',
          params.reschedule_schedule_id,
        );
      }
      return publicApiClient.get(
        `${apiConfig.endpoints.bookingLinks.getTimeSlots(token)}?${queryParams.toString()}`,
        {
          headers: verificationToken
            ? { 'X-Booking-Verification': verificationToken }
            : undefined,
        },
      );
    },
    book: (
      token: string,
      data: {
        session_type_id: string;
        date: string;
        time_slot_id?: string;
        time_slot_start?: string;
        time_slot_end?: string;
        notes?: string;
        reschedule_schedule_id?: string;
        action_token?: string;
      },
      verificationToken?: string,
    ) =>
      publicApiClient.post(apiConfig.endpoints.bookingLinks.book(token), data, {
        headers: verificationToken
          ? { 'X-Booking-Verification': verificationToken }
          : undefined,
      }),
  },
};

// Legacy method for backward compatibility (used in Projects.tsx)
export const getProjects = async (): Promise<ApiResponse<Project[]>> => {
  try {
    const response = await api.projects.list();
    return response.data;
  } catch (error: any) {
    console.error('Error fetching projects:', error);
    return {
      data: [],
      error:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch projects',
    };
  }
};

// Export the axios instance for custom requests
export {apiClient};

// Export the configuration
export {apiConfig, buildApiUrl, getEndpoint};

// Default export
export default api;
